import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const ButlerUniversityCaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="Butler Higher Ed | Curipod"
      subTitle="See how Curipod transformed a 50-student lecture hall at Butler University into an inclusive learning community — boosting engagement, participation, and student mood."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>CASE STUDY</HeroTag>
            <HeroTitle>From Lecture Hall to Learning Community</HeroTitle>
            <HeroSubtitle>Building Inclusive Engagement in Higher Education</HeroSubtitle>
            <HeroDescription>
              How Ashlee Tietje, an undergraduate biology lecturer at Butler University,
              used Curipod to transform a 50-student lecture into an interactive,
              inclusive learning experience — where every student has a voice.
            </HeroDescription>
            <HeroMeta>
              <MetaBadge>Butler University</MetaBadge>
              <MetaBadge>Higher Education</MetaBadge>
              <MetaBadge>Undergraduate Biology</MetaBadge>
            </HeroMeta>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <BenefitItem>
                  <BenefitIcon>✓</BenefitIcon>
                  <BenefitLabel>Boosted Student Mood</BenefitLabel>
                </BenefitItem>
                <BenefitItem>
                  <BenefitIcon>✓</BenefitIcon>
                  <BenefitLabel>Intellectual Engagement</BenefitLabel>
                </BenefitItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <BenefitItem>
                  <BenefitIcon>✓</BenefitIcon>
                  <BenefitLabel>Developed Inclusion</BenefitLabel>
                </BenefitItem>
                <BenefitItem>
                  <BenefitIcon>✓</BenefitIcon>
                  <BenefitLabel>Improved Participation</BenefitLabel>
                </BenefitItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* THE CHALLENGE */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid>
            <div>
              <SectionTag>THE CHALLENGE</SectionTag>
              <SectionTitle>From Lecture Hall to Learning Community</SectionTitle>
              <SectionText>
                <strong>Ashlee Tietje</strong>, an undergraduate biology lecturer at
                Butler University, knew that teaching a lecture-heavy course of nearly 50
                students came with challenges. &ldquo;My biggest thing is trying to keep
                students engaged during a lecture,&rdquo; she explained. &ldquo;I have a
                lot of content to get through during a short period of time.&rdquo;
              </SectionText>
              <SectionText>
                Her teaching philosophy has always balanced structure with heart.
                &ldquo;If I could be any teacher, I&apos;d be an amalgamation of Ms.
                Frizzle and Miss Honey,&rdquo; she said. But even with nearly two decades
                of experience, she wanted to keep finding new ways to make sure every
                student in her classroom participated, felt included, and had their voice
                heard.
              </SectionText>
              <SectionText>
                The turning point came after a campus panel on inclusive teaching, where
                students shared ideas about building classrooms where everyone is seen and
                heard. They suggested interactive polling — a small spark that set Ashlee
                on a path to discover Curipod.{' '}
                <strong>
                  What she found went far beyond polls. It gave her the tools to transform
                  her lecture hall into a true learning community.
                </strong>
              </SectionText>
            </div>

            <TeamCard>
              <TeamPhotoArea>
                <CircularImageWrap $size={150}>
                  <Image
                    src="/case-study-butler-university/ashlee-tietje.jpeg"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Ashlee Tietje, Undergraduate Biology Lecturer at Butler University"
                  />
                </CircularImageWrap>
                <TeamPhotoCaption>Ashlee Tietje</TeamPhotoCaption>
              </TeamPhotoArea>
              <TeamCardTitle>Butler Higher Ed</TeamCardTitle>
              <TeamMemberList>
                <TeamMember>
                  <TeamRole>Undergraduate Biology Lecturer</TeamRole>
                  <TeamName>Ashlee Tietje</TeamName>
                  <TeamNote>
                    &ldquo;An amalgamation of Ms. Frizzle and Miss Honey&rdquo;
                  </TeamNote>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Institution</TeamRole>
                  <TeamName>Butler University</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Course</TeamRole>
                  <TeamName>Undergraduate Biology</TeamName>
                  <TeamNote>~50 students per lecture section</TeamNote>
                </TeamMember>
              </TeamMemberList>
              <FocusAreaSection>
                <TeamRole>Focus Areas</TeamRole>
                <FocusTagRow>
                  {[
                    'Engagement in lecture-heavy courses',
                    'Inclusive classroom culture',
                    'Immediate feedback in a large section',
                  ].map(area => (
                    <FocusTag key={area}>{area}</FocusTag>
                  ))}
                </FocusTagRow>
              </FocusAreaSection>
            </TeamCard>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* BEYOND POLLING */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              BEYOND POLLING
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Active Learning at Scale
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              For Ashlee, Curipod quickly became more than just a polling tool. It
              transformed the rhythm of her lectures, giving students a voice from the
              very start. Each class began with a lighthearted question of the day —
              sometimes a poll, sometimes a drawing challenge — and students eagerly voted
              on their favorites.
            </SectionText>

            <ScreenshotGrid>
              <ScreenshotCard>
                <ScreenshotImageWrap>
                  <Image
                    src="/case-study-butler-university/curipod-polling.png"
                    layout="fill"
                    objectFit="contain"
                    alt="Curipod polling activity: What's the most bizarre food"
                  />
                </ScreenshotImageWrap>
                <ScreenshotCaption>
                  Interactive polling — students vote on lighthearted questions
                </ScreenshotCaption>
              </ScreenshotCard>
              <ScreenshotCard>
                <ScreenshotImageWrap>
                  <Image
                    src="/case-study-butler-university/curipod-biology.png"
                    layout="fill"
                    objectFit="contain"
                    alt="Curipod biology activity: Sketch a pair of duplicated homologous chromosomes"
                  />
                </ScreenshotImageWrap>
                <ScreenshotCaption>
                  Drawing challenges in biology — students sketch and learn together
                </ScreenshotCaption>
              </ScreenshotCard>
            </ScreenshotGrid>

            <BlockQuote style={{ maxWidth: 620, margin: '40px auto 0' }}>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                At the beginning of class, we have a question of the day&hellip; a
                poll&hellip; or a drawing question&hellip; then we voted on the best
                pictures.
              </QuoteText>
              <QuoteAttrib>— Ashlee Tietje, Undergraduate Biology Lecturer</QuoteAttrib>
            </BlockQuote>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* CHECK FOR UNDERSTANDING AT SCALE */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <SectionTag style={{ display: 'block', textAlign: 'center' }}>
            CHECK FOR UNDERSTANDING
          </SectionTag>
          <SectionTitle style={{ textAlign: 'center' }}>
            Feedback at Scale in a 50-Student Lecture
          </SectionTitle>
          <ImpactGrid>
            <ImpactCard>
              <ImpactIcon>💬</ImpactIcon>
              <ImpactTitle>AI-Powered Feedback for Every Student</ImpactTitle>
              <ImpactQuote>
                &ldquo;With a class of about 40 some students, I can&apos;t directly
                respond to every single student. AI feedback can give them an idea of
                where they are and what they need to work on.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Ashlee Tietje</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>🎯</ImpactIcon>
              <ImpactTitle>Every Student Has a Place</ImpactTitle>
              <ImpactQuote>
                &ldquo;Curipod&hellip; helped make sure my lessons are engaging and making
                sure everyone is involved and participating in class and feel like they
                have a place.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Ashlee Tietje</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>⭐</ImpactIcon>
              <ImpactTitle>Community Building</ImpactTitle>
              <ImpactQuote>
                &ldquo;Everybody in the class got to see everybody&apos;s compliments.
                That family dynamic is what we&apos;re going for.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Ashlee Tietje</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>🔬</ImpactIcon>
              <ImpactTitle>Joy in Complex Lessons</ImpactTitle>
              <ImpactQuote>
                From labeling DNA structures to debating polypeptide models, students
                weren&apos;t just memorizing — they were learning together, out loud.
              </ImpactQuote>
              <ImpactAttrib>— Course evaluation results</ImpactAttrib>
            </ImpactCard>
          </ImpactGrid>
        </MaxWidthContainer>
      </Section>

      {/* RESULTS */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              HIGHER ENGAGEMENT IN HIGHER EDUCATION
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              A Transformation Confirmed by Students
            </SectionTitle>
            <SectionText
              style={{
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                maxWidth: 720,
                margin: '0 auto 40px',
              }}
            >
              Ashlee reports that Curipod has &ldquo;really changed how I teach this past
              year.&rdquo; Students who once passively listened in a lecture now actively
              contribute through polls, drawings, and interactive tasks.
            </SectionText>

            <ResultBlockQuote>
              <QuoteMark style={{ color: theme.colors.orange }}>&ldquo;</QuoteMark>
              <QuoteText style={{ color: theme.colors.white }}>
                It engages students on the intellectual side and the content side, in
                developing inclusion, and building that family.
              </QuoteText>
              <QuoteAttrib style={{ color: theme.colors.orange }}>
                — Ashlee Tietje, Undergraduate Biology Lecturer
              </QuoteAttrib>
            </ResultBlockQuote>

            <ResultNote>
              Student course evaluations confirmed the difference. Many noted that Curipod
              supported their learning and even{' '}
              <strong>&ldquo;boosted their mood.&rdquo;</strong> Ashlee&apos;s evaluations
              reflected consistently high scores, particularly in student engagement and
              feeling respected.
            </ResultNote>

            <BenefitsGrid>
              {[
                'Boosted student mood',
                'Provided intellectual engagement',
                'Developed Inclusion',
                'Improved participation',
              ].map(benefit => (
                <BenefitCard key={benefit}>
                  <BenefitCheckmark>✓</BenefitCheckmark>
                  <BenefitText>{benefit}</BenefitText>
                </BenefitCard>
              ))}
            </BenefitsGrid>

            <ResultsStudentsPhoto>
              <CircularImageWrap $size={160} $borderColor="rgba(249,151,70,0.6)">
                <Image
                  src="/case-study-butler-university/lecture-hall.jpeg"
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center"
                  alt="Butler University lecture hall filled with students"
                />
              </CircularImageWrap>
              <ResultsPhotoLabel>
                Butler University undergraduate biology lecture
              </ResultsPhotoLabel>
            </ResultsStudentsPhoto>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* WHY IT MATTERS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              WHY IT MATTERS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>Why It Matters</SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              For Ashlee Tietje, Curipod didn&apos;t just add interactivity to her
              lectures. It fundamentally changed how students engage with each other and
              with the material. In a 50-student lecture hall, every student now has a
              voice, a place, and a reason to participate. That&apos;s the power of
              inclusive engagement at scale.
            </SectionText>

            <PeoplePhotoRow>
              <PhotoItem>
                <CircularImageWrap $size={110}>
                  <Image
                    src="/case-study-butler-university/ashlee-tietje.jpeg"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Ashlee Tietje"
                  />
                </CircularImageWrap>
                <PhotoLabel>Ashlee Tietje</PhotoLabel>
              </PhotoItem>
              <PhotoItem>
                <CircularImageWrap $size={110}>
                  <Image
                    src="/case-study-butler-university/lecture-hall.jpeg"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Butler University lecture hall"
                  />
                </CircularImageWrap>
                <PhotoLabel>Lecture hall</PhotoLabel>
              </PhotoItem>
            </PeoplePhotoRow>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                It&apos;s really changed how I teach this past year.
              </FinalQuoteText>
              <QuoteAttrib>— Ashlee Tietje, Undergraduate Biology Lecturer</QuoteAttrib>
            </FinalQuoteCard>

            <TagRow>
              {['Engagement', 'Inclusion', 'Feedback', 'Community'].map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagRow>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIcon aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIcon>
            <DownloadText>
              <DownloadTitle>Download the Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the Butler University case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="/Curipod_Case_Study_Butler_University.pdf"
              download="Curipod_Case_Study_Butler_University.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default ButlerUniversityCaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 20px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  justify-content: center;
  flex-wrap: nowrap;

  @media ${MOBILE} {
    flex-wrap: wrap;
    gap: 16px;
    justify-content: center;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 28px;
  justify-self: center;
  flex-shrink: 0;

  @media ${MOBILE} {
    width: 60px;
    height: 1px;
    margin: 0 auto;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.25);
`;

const BenefitIcon = styled.span`
  font-size: 20px;
  font-weight: bold;
  color: ${theme.colors.white};
`;

const BenefitLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    font-size: 13px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── CIRCULAR IMAGE ─────────────────────────────────────────────────────────── */

const CircularImageWrap = styled.div<{ $size: number; $borderColor?: string }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid ${({ $borderColor }) => $borderColor ?? theme.colors.orange};
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

/* ─── CHALLENGE SECTION ──────────────────────────────────────────────────────── */

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

const TeamCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const TeamPhotoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 2px solid ${theme.colors.beigeDark};
`;

const TeamPhotoCaption = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  margin-top: 10px;
  font-style: italic;
  text-align: center;
`;

const TeamCardTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const TeamMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamMember = styled.div``;

const TeamRole = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const TeamName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

const TeamNote = styled.div`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  margin-top: 2px;
`;

const FocusAreaSection = styled.div`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 2px solid ${theme.colors.beigeDark};
`;

const FocusTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const FocusTag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 14px;
  border: 1px solid ${theme.colors.orange};
`;

/* ─── SCREENSHOTS ────────────────────────────────────────────────────────────── */

const ScreenshotGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  width: 100%;
  max-width: 900px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ScreenshotCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 16px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
`;

const ScreenshotImageWrap = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 52%;
  border-radius: 8px;
  overflow: hidden;
`;

const ScreenshotCaption = styled.p`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  text-align: center;
  margin: 12px 0 0;
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div<{ $wide?: boolean }>`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactQuote = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactAttrib = styled.cite`
  font-size: 13px;
  font-weight: bold;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultBlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 0 0 48px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 10px 10px 0;
  max-width: 640px;
  width: 100%;
`;

const ResultNote = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  max-width: 680px;
  line-height: 1.75;
  text-align: center;
  margin: 0 0 48px;

  strong {
    color: ${theme.colors.orange};
    font-weight: 700;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  max-width: 560px;
  width: 100%;
  margin-bottom: 48px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(249, 151, 70, 0.3);
  border-radius: 12px;
  padding: 14px 20px;
`;

const BenefitCheckmark = styled.span`
  color: ${theme.colors.orange};
  font-size: 20px;
  font-weight: bold;
  flex-shrink: 0;
`;

const BenefitText = styled.span`
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: 600;
`;

const ResultsStudentsPhoto = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ResultsPhotoLabel = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  text-align: center;
  margin: 0;
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const PeoplePhotoRow = styled.div`
  display: flex;
  gap: 32px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 48px;

  @media ${MOBILE} {
    gap: 20px;
    margin-bottom: 32px;
  }
`;

const PhotoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const PhotoLabel = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  text-align: center;
  margin: 0;
`;

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.4;
  margin: 0 0 16px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Tag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIcon = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
